import React, { useEffect, useState } from "react";
import { ArrowRight, Bot, CheckCircle2, Sparkles } from "lucide-react";
import Reveal from "./Reveal";
import SectionHeader from "./SectionHeader";
import Counter from "./Counter";
import { useInView } from "../hooks/useInView";

const storyIntro =
  "I'm an AI/ML Engineer building intelligent systems that reason, automate, and deliver real-world value — from agentic AI and RAG pipelines to full-stack AI applications.";

const storyHighlights = [
  "10+ AI/ML projects with production-focused implementations",
  "Building with LangChain, LangGraph, n8n, MCP, and modern LLMs",
  "Full-stack engineering across AI, backend, and frontend systems",
  "Focused on AI automation, intelligent workflows, and business solutions",
];

const coreStrengths = [
  {
    gid: "ring-agentic",
    from: "#2dd4bf",
    to: "#34d399",
    glow: "rgba(45,212,191,0.55)",
    label: "Agentic AI & Automation",
    blurb: "Multi-step agents that act, decide & automate",
    level: 95,
  },
  {
    gid: "ring-rag",
    from: "#34d399",
    to: "#2dd4bf",
    glow: "rgba(52,211,153,0.55)",
    label: "RAG & Vector Search",
    blurb: "Embeddings, FAISS & retrieval pipelines",
    level: 92,
  },
  {
    gid: "ring-dl",
    from: "#14b8a6",
    to: "#2dd4bf",
    glow: "rgba(20,184,166,0.55)",
    label: "Deep Learning & Vision",
    blurb: "CNNs & TensorFlow / Keras models",
    level: 88,
  },
  {
    gid: "ring-fullstack",
    from: "#10b981",
    to: "#14b8a6",
    glow: "rgba(16,185,129,0.55)",
    label: "Building Fast & Full-Stack",
    blurb: "FastAPI, React & production-ready APIs",
    level: 90,
  },
];

const SkillRing = ({
  gid,
  from,
  to,
  glow,
  label,
  blurb,
  level,
  size = 92,
  stroke = 8,
  delay = 0,
}) => {
  const { ref, inView } = useInView({ threshold: 0.4 });
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const target = circumference - (level / 100) * circumference;
  const [offsetNow, setOffset] = useState(circumference);

  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => setOffset(target), 120 + delay);
    return () => clearTimeout(t);
  }, [inView, target, delay]);

  return (
    <div
      ref={ref}
      className="cursor-target group relative rounded-2xl border border-border bg-card/80 p-3.5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10"
    >
      <div
        className="pointer-events-none absolute -top-10 -right-10 h-20 w-20 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-25"
        style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
      />
      <div className="relative mx-auto h-[92px] w-[92px]">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="-rotate-90"
        >
          <defs>
            <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={from} />
              <stop offset="100%" stopColor={to} />
            </linearGradient>
          </defs>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="hsl(var(--secondary))"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={`url(#${gid})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offsetNow}
            style={{
              transition: "stroke-dashoffset 1.5s cubic-bezier(0.22,1,0.36,1)",
              transitionDelay: `${delay}ms`,
              filter: `drop-shadow(0 0 6px ${glow})`,
            }}
          />
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <span className="font-display text-lg font-bold">
            <Counter end={level} suffix="%" />
          </span>
        </div>
      </div>
      <div className="relative mt-3 text-center">
        <div className="flex items-center justify-center gap-1.5 text-[13px] font-semibold">
          {label}
          <Sparkles
            size={11}
            className="text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          />
        </div>
        <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{blurb}</div>
      </div>
    </div>
  );
};

const About = () => {
  return (
    <section id="about" className="relative py-10 md:py-12 overflow-hidden min-h-screen flex items-center">
      <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="01."
          eyebrow="About"
          title="Turning ideas into intelligent systems"
          description="A quick look at who I am, what I build, and the impact I aim to create."
        />

        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-8 lg:gap-12 items-center">
          {/* ---------- Left — story ---------- */}
          <Reveal className="space-y-5">
            <p className="border-l-2 border-primary/50 pl-4 md:pl-5 text-sm md:text-base leading-relaxed text-muted-foreground">
              {storyIntro}
            </p>

            <div className="space-y-2.5">
              {storyHighlights.map((highlight, index) => (
                <div key={index} className="group flex items-start gap-3">
                  <span className="mt-0.5 rounded-full bg-primary/10 p-0.5 text-primary">
                    <CheckCircle2
                      size={18}
                      className="transition-transform duration-300 group-hover:scale-110"
                    />
                  </span>
                  <span className="text-foreground/90 text-sm md:text-base font-medium">
                    {highlight}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="glow-border rounded-2xl p-4 md:p-5 bg-card/60 backdrop-blur-sm">
              <div className="mb-2 flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md">
                  <Bot size={16} />
                </span>
                <h4 className="font-display text-base md:text-lg font-semibold">
                  Have a bold idea? Let&apos;s give it intelligence.
                </h4>
              </div>
              <p className="mb-3 text-sm text-muted-foreground">
                I&apos;m always up for collaborating on ambitious AI projects that solve
                real-world problems.
              </p>
              <button
                onClick={() =>
                  document.getElementById("contact").scrollIntoView({ behavior: "smooth" })
                }
                className="cursor-target group inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                Get In Touch
                <ArrowRight
                  size={15}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>
          </Reveal>

          {/* ---------- Right — core strengths ---------- */}
          <Reveal delay={120}>
            <div className="rounded-2xl border border-border bg-card/60 p-4 md:p-5 backdrop-blur-sm">
              <div className="mb-4 flex items-center justify-center gap-2">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                <h4 className="font-display text-base md:text-lg font-semibold">
                  Core Strengths
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {coreStrengths.map((strength, index) => (
                  <SkillRing key={strength.gid} {...strength} delay={index * 120} />
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;